import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';

type AIJob = {
  id: string;
  provider: string;
  model: string;
  job_type: string;
  status: string;
  prompt: string | null;
  preview_url: string | null;
  output_urls: string[];
  cost_usd: number | null;
  error: string | null;
  target_kind: string | null;
  target_id: string | null;
  created_at: string;
  completed_at: string | null;
};

const LUTRON_LINES = [
  { slug: 'caseta',    name: 'Caséta' },
  { slug: 'radiora3',  name: 'RadioRA 3' },
  { slug: 'maestro',   name: 'Maestro' },
  { slug: 'wallbox',   name: 'Wallbox' },
  { slug: 'vive',      name: 'Vive' },
  { slug: 'homeworks', name: 'HomeWorks' },
];

export function AdminAIContent() {
  const [tab, setTab] = useState<'upscale' | 'hero' | 'reel' | 'jobs'>('jobs');
  const [jobs, setJobs] = useState<AIJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [providerStatus, setProviderStatus] = useState<'unknown' | 'stub' | 'real'>('unknown');

  useEffect(() => { reload(); }, []);

  async function reload() {
    setLoading(true);
    const { data } = await supabase.from('ai_jobs')
      .select('*').order('created_at', { ascending: false }).limit(50);
    const rows = (data as AIJob[]) ?? [];
    setJobs(rows);
    setProviderStatus(rows.some(j => j.provider === 'higgsfield') ? 'real' : rows.some(j => j.provider === 'stub') ? 'stub' : 'unknown');
    setLoading(false);
  }

  return (
    <AdminContainer
      title="Contenido AI · Higgsfield"
      eyebrow="Mejora fotos · genera hero · genera reels"
    >
      <ProviderBanner status={providerStatus} />

      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: `1px solid ${theme.border}` }}>
        <TabBtn active={tab === 'jobs'}    onClick={() => setTab('jobs')}>📋 Historial de jobs</TabBtn>
        <TabBtn active={tab === 'upscale'} onClick={() => setTab('upscale')}>🔍 Mejorar fotos producto</TabBtn>
        <TabBtn active={tab === 'hero'}    onClick={() => setTab('hero')}>🌅 Generar hero Lutron</TabBtn>
        <TabBtn active={tab === 'reel'}    onClick={() => setTab('reel')}>🎬 Generar reel</TabBtn>
      </div>

      {tab === 'jobs'    && <JobsList jobs={jobs} loading={loading} onRefresh={reload} />}
      {tab === 'upscale' && <UpscalePanel onSubmitted={reload} />}
      {tab === 'hero'    && <HeroPanel onSubmitted={reload} />}
      {tab === 'reel'    && <ReelPanel onSubmitted={reload} />}
    </AdminContainer>
  );
}

function ProviderBanner({ status }: { status: 'unknown' | 'stub' | 'real' }) {
  if (status === 'real') {
    return (
      <div style={banner(theme.accentDark, 'rgba(87,255,154,0.06)')}>
        <strong>✓ Higgsfield API activo</strong> · cada job se procesa real con cargo a tu cuenta Higgsfield.
      </div>
    );
  }
  if (status === 'stub') {
    return (
      <div style={banner(theme.warn, 'rgba(245,158,11,0.06)')}>
        <strong>⚠ Modo STUB</strong> · Higgsfield API key no configurada en secrets. Los jobs avanzan a "completed" con placeholders descriptivos para probar el flujo. <br />
        Para activar real: en Supabase Dashboard → Project Settings → Edge Functions → Secrets, agrega <code>HIGGSFIELD_KEY_ID</code> y <code>HIGGSFIELD_KEY_SECRET</code>.
      </div>
    );
  }
  return (
    <div style={banner(theme.textMuted, theme.bgFooter)}>
      Aún no has corrido ningún job. Empieza con un upscale de prueba abajo.
    </div>
  );
}

const banner = (color: string, bg: string): React.CSSProperties => ({
  padding: 14,
  background: bg,
  border: `1px solid ${color}`,
  borderLeft: `4px solid ${color}`,
  color: theme.brand,
  fontSize: 13,
  lineHeight: 1.6,
  marginBottom: 20,
});

function JobsList({ jobs, loading, onRefresh }: { jobs: AIJob[]; loading: boolean; onRefresh: () => void }) {
  if (loading) return <div style={{ color: theme.textMuted }}>Cargando…</div>;
  if (jobs.length === 0) return (
    <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, border: `1px dashed ${theme.border}` }}>
      Sin jobs todavía. Usa los tabs para crear el primero.
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: theme.textMuted }}>{jobs.length} jobs recientes</div>
        <button onClick={onRefresh} style={btnSecondary}>↻ Refrescar</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        {jobs.map(j => <JobCard key={j.id} job={j} />)}
      </div>
    </div>
  );
}

function JobCard({ job }: { job: AIJob }) {
  const statusColor = {
    pending:    theme.textMuted,
    processing: theme.warn,
    completed:  theme.accentDark,
    failed:     theme.err,
    cancelled:  theme.textMuted,
  }[job.status] ?? theme.textMuted;

  const isVideo = job.job_type === 'reel_video';
  return (
    <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column' }}>
      <div style={{
        aspectRatio: '4/3', position: 'relative',
        background: theme.bgImage,
        backgroundImage: job.preview_url && !isVideo ? `url(${job.preview_url})` : undefined,
        backgroundSize: 'cover', backgroundPosition: 'center',
      }}>
        {isVideo && job.preview_url && (
          <video src={job.preview_url} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        {!job.preview_url && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: theme.textMuted, fontSize: 11, padding: 10, textAlign: 'center',
          }}>
            {job.status === 'processing' ? '⏳ Procesando…' : job.status === 'failed' ? '✗ Falló' : '—'}
          </div>
        )}
        <div style={{
          position: 'absolute', top: 6, left: 6,
          padding: '2px 6px', fontSize: 9, fontWeight: 700,
          background: statusColor, color: '#fff',
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          {job.status}
        </div>
      </div>
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.brand }}>{job.job_type}</div>
        <div style={{ fontSize: 9, color: theme.textMuted, fontFamily: theme.font.mono, marginTop: 2 }}>
          {job.model} · {job.provider}
        </div>
        {job.prompt && <div style={{ fontSize: 11, color: theme.text, marginTop: 6, lineHeight: 1.3, maxHeight: 40, overflow: 'hidden' }}>
          {job.prompt.slice(0, 80)}
        </div>}
        {job.error && <div style={{ fontSize: 10, color: theme.err, marginTop: 6 }}>{job.error.slice(0, 80)}</div>}
      </div>
    </div>
  );
}

// =================== UPSCALE ===================
function UpscalePanel({ onSubmitted }: { onSubmitted: () => void }) {
  const [products, setProducts] = useState<{ id: string; sku: string; name: string; img: string | null }[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    supabase.from('products_public')
      .select('id, sku, name, images')
      .eq('status', 'active')
      .limit(100)
      .then(({ data }) => {
        setProducts(((data as any[]) ?? []).map(p => ({
          id: p.id, sku: p.sku, name: p.name,
          img: (p.images?.[0]?.url) ?? null,
        })));
      });
  }, []);

  async function submit() {
    if (!selectedId) return;
    const p = products.find(x => x.id === selectedId);
    if (!p?.img) { setMsg('Producto sin foto base'); return; }
    setBusy(true); setMsg(null);

    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        job_type: 'upscale',
        prompt: `Enhance product photo of ${p.name} (${p.sku}) to 4K, preserve product identity, professional lighting.`,
        input_refs: [p.img],
        params: { scale: 4 },
        target_kind: 'product',
        target_id: p.id,
        apply_to_kind: 'product_image',
      }),
    });
    const data = await res.json();
    setBusy(false);
    if (data.ok) {
      setMsg(`✓ Job ${data.job_id} ${data.stub ? '(STUB)' : 'enviado'} · ${data.message ?? ''}`);
      onSubmitted();
    } else {
      setMsg(`✗ ${data.error}`);
    }
  }

  const sel = products.find(p => p.id === selectedId);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
      <div>
        <Label>Producto a mejorar</Label>
        <div style={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${theme.border}` }}>
          {products.map(p => (
            <button key={p.id} onClick={() => setSelectedId(p.id)} style={{
              display: 'flex', gap: 10, alignItems: 'center', width: '100%',
              padding: 10, textAlign: 'left',
              background: selectedId === p.id ? theme.bgFooter : theme.bg,
              borderLeft: selectedId === p.id ? `4px solid ${theme.accent}` : '4px solid transparent',
              border: 'none', borderBottom: `1px solid ${theme.border}`,
              cursor: 'pointer', fontFamily: theme.font.sans,
            }}>
              <div style={{
                width: 48, height: 48, background: theme.bgImage,
                backgroundImage: p.img ? `url(${p.img})` : undefined,
                backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.brand }}>{p.name}</div>
                <div style={{ fontSize: 10, color: theme.textMuted, fontFamily: theme.font.mono }}>{p.sku}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label>Preview de la foto actual</Label>
        {sel?.img ? (
          <>
            <div style={{
              aspectRatio: '1/1', background: theme.bgImage,
              backgroundImage: `url(${sel.img})`,
              backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
              border: `1px solid ${theme.border}`,
            }} />
            <div style={{ marginTop: 14, padding: 12, background: theme.bgFooter, fontSize: 12, color: theme.textMuted, lineHeight: 1.5 }}>
              Al mejorar: Soul Upscale 4× → ~2048px · genera foto profesional manteniendo identidad del producto · costo ~$0.05–0.10 USD · ~10–15 segundos
            </div>
            <button onClick={submit} disabled={busy} style={{ ...btnPrimary, marginTop: 16, width: '100%' }}>
              {busy ? 'Enviando…' : '🔍 Mejorar a 4K →'}
            </button>
            {msg && <div style={{ marginTop: 12, fontSize: 12, color: theme.brand, padding: 10, background: theme.bgFooter }}>{msg}</div>}
          </>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, border: `1px dashed ${theme.border}` }}>
            Selecciona un producto a la izquierda
          </div>
        )}
      </div>
    </div>
  );
}

// =================== HERO LUTRON ===================
function HeroPanel({ onSubmitted }: { onSubmitted: () => void }) {
  const [lineSlug, setLineSlug] = useState('caseta');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const line = LUTRON_LINES.find(l => l.slug === lineSlug)!;

  // Prompts predefinidos por tier (después se puede personalizar)
  const PROMPT_TEMPLATES: Record<string, string> = {
    caseta:    'Modern living room with elegant Lutron Caseta wireless dimmers on the wall, warm ambient lighting, scandinavian interior design, consumer friendly aesthetic, cinematic photography, 4K',
    radiora3:  'Contemporary luxury living room with Lutron Sunnata touch keypads in satin nickel, sophisticated ambient mood lighting, modern architecture, editorial photography, premium feel',
    maestro:   'Professional home interior with Lutron Maestro dimmers, classic American residential style, warm workhorse aesthetic, photographed for architectural digest',
    wallbox:   'Standard home installation with Lutron Diva dimmers, bright accessible interior, modern apartment style, mass market lighting',
    vive:      'Modern corporate office with Lutron Vive engraved keypads, conference room with daylight harvesting, commercial spec-grade environment, clean professional',
    homeworks: 'Ultra-luxury penthouse with Lutron Palladiom brass keypads, marble walls, jewelry-grade architecture, editorial Architectural Digest aesthetic, golden hour lighting',
  };

  const effectivePrompt = prompt.trim() || PROMPT_TEMPLATES[lineSlug];

  async function submit() {
    setBusy(true); setMsg(null);
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        job_type: 'hero_image',
        prompt: effectivePrompt,
        params: { aspect_ratio: '21:9', style: 'cinematic', quality: 'high' },
        target_kind: 'lutron_line',
        target_id: lineSlug,
        apply_to_kind: 'lutron_asset',
      }),
    });
    const data = await res.json();
    setBusy(false);
    if (data.ok) {
      setMsg(`✓ Generación ${data.stub ? '(STUB)' : 'enviada'}. ${data.message ?? ''}`);
      onSubmitted();
    } else { setMsg(`✗ ${data.error}`); }
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <Field label="Línea Lutron">
        <select value={lineSlug} onChange={e => setLineSlug(e.target.value)} style={inputStyle}>
          {LUTRON_LINES.map(l => <option key={l.slug} value={l.slug}>{l.name}</option>)}
        </select>
      </Field>

      <div style={{ marginTop: 14 }}>
        <Field label={`Prompt (deja vacío para usar template de ${line.name})`}>
          <textarea
            rows={4}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder={PROMPT_TEMPLATES[lineSlug]}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: theme.font.sans }}
          />
        </Field>
      </div>

      <div style={{
        marginTop: 14, padding: 12,
        background: theme.bgFooter, border: `1px solid ${theme.border}`,
        fontSize: 12, color: theme.textMuted, lineHeight: 1.6,
      }}>
        <strong style={{ color: theme.brand }}>Prompt efectivo:</strong>
        <div style={{ fontFamily: theme.font.mono, fontSize: 11, marginTop: 6, color: theme.brand }}>
          {effectivePrompt}
        </div>
      </div>

      <div style={{ marginTop: 16, fontSize: 12, color: theme.textMuted }}>
        Modelo: <code>Soul 2.0</code> · aspect ratio 21:9 (panorámico hero) · ~15s · costo ~$0.20–0.30 USD<br />
        Al completar, se guarda como <code>kind=hero</code> en lutron_assets y aparece automáticamente en <code>/lutron/{lineSlug}</code>
      </div>

      <button onClick={submit} disabled={busy} style={{ ...btnPrimary, marginTop: 20 }}>
        {busy ? 'Generando…' : `🌅 Generar hero para ${line.name} →`}
      </button>

      {msg && <div style={{ marginTop: 12, fontSize: 12, color: theme.brand, padding: 10, background: theme.bgFooter }}>{msg}</div>}
    </div>
  );
}

// =================== REEL ===================
function ReelPanel({ onSubmitted }: { onSubmitted: () => void }) {
  const [lineSlug, setLineSlug] = useState('caseta');
  const [motion, setMotion] = useState('dolly_in');
  const [duration, setDuration] = useState(5);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const line = LUTRON_LINES.find(l => l.slug === lineSlug)!;

  async function submit() {
    setBusy(true); setMsg(null);
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        job_type: 'reel_video',
        prompt: prompt || `Smooth ${motion} camera move showing Lutron ${line.name} keypad illuminating a modern room, cinematic, 4K`,
        params: { duration_seconds: duration, motion, aspect_ratio: '9:16', style: 'cinematic' },
        target_kind: 'lutron_line',
        target_id: lineSlug,
        apply_to_kind: 'lutron_asset',
      }),
    });
    const data = await res.json();
    setBusy(false);
    if (data.ok) {
      setMsg(`✓ Generación ${data.stub ? '(STUB)' : 'enviada'} · video tarda 1–3 min · ${data.message ?? ''}`);
      onSubmitted();
    } else { setMsg(`✗ ${data.error}`); }
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Línea Lutron">
          <select value={lineSlug} onChange={e => setLineSlug(e.target.value)} style={inputStyle}>
            {LUTRON_LINES.map(l => <option key={l.slug} value={l.slug}>{l.name}</option>)}
          </select>
        </Field>
        <Field label="Movimiento de cámara">
          <select value={motion} onChange={e => setMotion(e.target.value)} style={inputStyle}>
            <option value="dolly_in">Dolly In (acercamiento suave)</option>
            <option value="dolly_out">Dolly Out (alejamiento)</option>
            <option value="orbit">Orbit (gira alrededor)</option>
            <option value="crane_up">Crane Up (sube)</option>
            <option value="tilt">Tilt (inclinación)</option>
            <option value="fpv">FPV (primera persona)</option>
          </select>
        </Field>
      </div>

      <div style={{ marginTop: 14 }}>
        <Field label="Duración">
          <select value={duration} onChange={e => setDuration(Number(e.target.value))} style={inputStyle}>
            <option value={5}>5 segundos (~$0.50)</option>
            <option value={10}>10 segundos (~$1.00)</option>
          </select>
        </Field>
      </div>

      <div style={{ marginTop: 14 }}>
        <Field label="Prompt (opcional)">
          <textarea
            rows={3}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder={`Smooth ${motion} camera move showing Lutron ${line.name} keypad illuminating a modern room`}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: theme.font.sans }}
          />
        </Field>
      </div>

      <div style={{ marginTop: 14, padding: 12, background: theme.bgFooter, fontSize: 12, color: theme.textMuted, lineHeight: 1.6 }}>
        Modelo: <code>DoP</code> · 9:16 vertical (Instagram Reels / TikTok) · {duration}s · ~$0.{duration === 5 ? '50' : '100'} USD · 1–3 min de generación
      </div>

      <button onClick={submit} disabled={busy} style={{ ...btnPrimary, marginTop: 20 }}>
        {busy ? 'Encolando…' : `🎬 Generar reel de ${duration}s →`}
      </button>

      {msg && <div style={{ marginTop: 12, fontSize: 12, color: theme.brand, padding: 10, background: theme.bgFooter }}>{msg}</div>}
    </div>
  );
}

// =================== HELPERS ===================
function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent',
      color: active ? theme.brand : theme.textMuted,
      border: 'none',
      borderBottom: `2px solid ${active ? theme.accent : 'transparent'}`,
      padding: '12px 18px',
      fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
      cursor: 'pointer', fontFamily: theme.font.sans, marginBottom: -1,
    }}>{children}</button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{
        fontSize: 11, color: theme.textMuted,
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
        marginBottom: 6,
      }}>{label}</div>
      {children}
    </label>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <Field label={String(children)}>{null}</Field>;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  padding: '10px 12px',
  color: theme.text,
  fontSize: 13,
  fontFamily: theme.font.sans,
  outline: 'none',
  boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  background: theme.accent,
  color: theme.brand,
  border: 'none',
  padding: '12px 20px',
  fontSize: 12, fontWeight: 700,
  letterSpacing: 0.4, textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: theme.font.sans,
};

const btnSecondary: React.CSSProperties = {
  background: 'transparent', color: theme.brand,
  border: `1px solid ${theme.border}`,
  padding: '8px 14px',
  fontSize: 11, fontWeight: 700,
  letterSpacing: 0.4, textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: theme.font.sans,
};
